import { useState } from "react";
import { useTranslation } from "react-i18next";
import ArticlePhotoStrip from "@/pages/nostalgie/components/ArticlePhotoStrip";
import type { GalleryImage } from "@/components/feature/GalleryGrid";

export type PressArticleBlock = { type: "p" | "h"; text: string };

interface PressArticleFeatureProps {
  index: number;
  blocks: PressArticleBlock[];
  source: string;
  standfirst?: string;
  bylineKey: string;
  photos?: GalleryImage[];
}

export default function PressArticleFeature({
  index,
  blocks,
  source,
  standfirst,
  bylineKey,
  photos,
}: PressArticleFeatureProps) {
  const { t, i18n } = useTranslation();
  const isDe = (i18n.language || "de").startsWith("de");
  const [open, setOpen] = useState(false);

  return (
    <article className="md:col-span-2 xl:col-span-3 bg-background-100 border border-background-200 rounded-lg overflow-hidden">
      <div className="p-6 md:p-8 flex flex-col">
        <div className="flex items-center gap-3 mb-4">
          <span className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 shrink-0">
            <i className="ri-newspaper-line text-lg"></i>
          </span>
          <div className="min-w-0">
            <div className="text-xs font-semibold tracking-[0.2em] text-accent-700">
              {t(`nost_press_${index}_year`)}
            </div>
            <div className="text-sm text-foreground-600 leading-snug">
              {t(`nost_press_${index}_source`)}
            </div>
          </div>
        </div>

        <h3 className="font-heading text-2xl md:text-3xl text-foreground-950 leading-snug mb-3">
          {t(`nost_press_${index}_title`)}
        </h3>

        <p className="text-foreground-700 leading-relaxed text-sm md:text-base mb-4">
          {t(`nost_press_${index}_text`)}
        </p>

        <div className="text-xs uppercase tracking-[0.2em] text-foreground-500 mb-6">
          {t(bylineKey)}
        </div>

        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          className="self-start inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-md text-sm font-semibold uppercase tracking-wider whitespace-nowrap cursor-pointer transition-colors"
        >
          {open ? t("nost_press_article_close") : t("nost_press_article_open")}
          <i className={open ? "ri-arrow-up-line" : "ri-arrow-down-line"}></i>
        </button>
      </div>

      {open && (
        <div className="border-t border-background-200 px-6 md:px-8 py-8 md:py-9">
          {!isDe && (
            <p className="mb-5 text-xs uppercase tracking-[0.2em] text-foreground-500">
              {t("nost_press_article_lang_note")}
            </p>
          )}

          {standfirst && (
            <p className="max-w-2xl md:mx-auto font-heading text-sm md:text-[15px] text-foreground-900 leading-relaxed mb-5">
              {standfirst}
            </p>
          )}

          <div className="max-w-2xl md:mx-auto">
            {blocks.map((block, position) =>
              block.type === "h" ? (
                <h4
                  key={`${block.type}-${position}`}
                  className="font-heading text-base md:text-[15px] text-foreground-950 leading-snug mt-5 mb-2"
                >
                  {block.text}
                </h4>
              ) : (
                <p
                  key={`${block.type}-${position}`}
                  className="text-foreground-600 leading-relaxed text-sm md:text-[13px] mb-3"
                >
                  {block.text}
                </p>
              ),
            )}
          </div>

          <p className="mt-6 max-w-2xl md:mx-auto text-foreground-500 text-xs leading-relaxed border-l-2 border-accent-300 pl-4">
            {source}
          </p>

          {photos && photos.length > 0 && (
            <div className="mt-6 max-w-2xl md:mx-auto">
              <div className="text-xs uppercase tracking-[0.2em] text-foreground-500 mb-3">
                {t("nost_press_photos_label")}
              </div>
              <ArticlePhotoStrip photos={photos} />
            </div>
          )}
        </div>
      )}
    </article>
  );
}